import SectionService from "utilities/services/sections/section-service";
import SectionRecord from "models/view-models/section-record";
import SectionCollectionRecord from "models/view-models/section-collection-record";
import { useMemo } from "react";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import SharedPageSectionService from "utilities/services/shared-page-sections/shared-page-section-service";
import PublicationService from "utilities/services/publications/publication-service";
import PublicPublicationService from "utilities/services/public/publications/publication-service";
import StringUtils from "utilities/string-utils";

interface UseCodeReferenceBlockSectionApi {
    section: SectionRecord | undefined;
    loadingCodeReference: boolean;
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook to get a section with its sub-sections and its publication.
 */
export default function useCodeReferenceBlockSectionApi(
    sectionId?: number
): UseCodeReferenceBlockSectionApi {
    const { get: getSectionQuery } = SectionService.useGetQuery();
    const { list: listSubSectionsQuery } =
        SectionService.useSubSectionListQuery();
    const { get: getSharedSectionQuery } =
        SharedPageSectionService.useGetQuery();
    const { list: listSharedSubSectionsQuery } =
        SharedPageSectionService.useSubSectionListQuery();
    const { pageGuid, recipientGuid } = useSharedPageContext();
    const isPublic = useMemo(() => !StringUtils.isEmpty(pageGuid), [pageGuid]);
    const hasValidSectionId = sectionId != null && sectionId > 0;

    const privateSectionResponse = getSectionQuery(
        { id: sectionId ?? 0 },
        { ignoreQueryFilters: true },
        {
            enabled: hasValidSectionId && !isPublic,
            staleTime: STALE_TIME,
        }
    );
    const privateSubSectionsResponse = listSubSectionsQuery(
        { parentId: sectionId ?? 0 },
        {
            enabled: hasValidSectionId && !isPublic,
            staleTime: STALE_TIME,
        }
    );

    const sharedSectionResponse = getSharedSectionQuery(
        {
            sectionId: sectionId ?? 0,
            pageGuid: pageGuid ?? "",
        },
        {
            recipient: recipientGuid,
        },
        {
            enabled: hasValidSectionId && isPublic,
            staleTime: STALE_TIME,
        }
    );
    const sharedSubSectionsResponse = listSharedSubSectionsQuery(
        {
            pageGuid: pageGuid ?? "",
        },
        {
            parentId: sectionId,
            recipient: recipientGuid,
        },
        {
            enabled: hasValidSectionId && isPublic,
            staleTime: STALE_TIME,
        }
    );

    const activeSectionResponse = isPublic
        ? sharedSectionResponse
        : privateSectionResponse;
    const activeSubSectionsResponse = isPublic
        ? sharedSubSectionsResponse
        : privateSubSectionsResponse;

    const section = activeSectionResponse.data?.resultObject;
    const subSections = useMemo(
        () => activeSubSectionsResponse.data?.resultObjects ?? [],
        [activeSubSectionsResponse.data?.resultObjects]
    );

    const publicationService = isPublic
        ? PublicPublicationService
        : PublicationService;
    const { get: getPublicationQuery } = publicationService.useGetQuery();
    const publicationResponse = getPublicationQuery(
        { id: section?.publicationId ?? 0 },
        { ignoreQueryFilters: true },
        {
            enabled: section?.publicationId != null,
        }
    );

    const publication = publicationResponse.data?.resultObject;

    const sectionWithSubSectionsAndPublication = useMemo(() => {
        if (section == null || publication == null) {
            return undefined;
        }

        const subSectionsWithPublications = subSections.map(
            (subSection: SectionRecord) =>
                subSection.with({
                    publication: publication,
                })
        );

        const sections = [
            section.with({
                parentId: undefined,
                publication: publication,
            }),
            ...subSectionsWithPublications,
        ];

        return new SectionCollectionRecord({
            parts: [],
            sections,
        })
            .buildSectionHierarchy()
            .find((activeSection) => activeSection.id === section.id);
    }, [section, subSections, publication]);

    const loadingCodeReference =
        activeSectionResponse.isLoading ||
        activeSubSectionsResponse.isLoading ||
        publicationResponse.isLoading;

    return {
        section: sectionWithSubSectionsAndPublication,
        loadingCodeReference,
    };
}
